import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../api/client'
import { Badge, Loading } from '../../components/ui'

interface AdminTransaction {
  id: string
  userId: string
  amount: number
  type: string
  description: string | null
  referenceId: string | null
  createdAt: string
  user: {
    id: string
    name: string
    email: string
  }
}

interface TransactionsResponse {
  transactions: AdminTransaction[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function AdminTransactionsPage() {
  const [transactions, setTransactions] = useState<AdminTransaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchTransactions()
  }, [typeFilter, page])

  const fetchTransactions = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (typeFilter) params.append('type', typeFilter)
      params.append('page', String(page))
      params.append('limit', '30')

      const data = await api.get<TransactionsResponse>(`/admin/transactions?${params}`)
      setTransactions(data.transactions)
      setTotalPages(data.totalPages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions')
    } finally {
      setIsLoading(false)
    }
  }

  const getTypeVariant = (type: string): 'green' | 'blue' | 'gold' | 'gray' | 'red' => {
    switch (type) {
      case 'EARNED':
      case 'PRODUCT_SALE':
        return 'green'
      case 'SPENT':
      case 'PRODUCT_PURCHASE':
        return 'blue'
      case 'WITHDRAWAL':
        return 'gold'
      case 'ADMIN_GRANT':
        return 'gold'
      case 'DEPOSIT':
        return 'green'
      case 'REFUND':
        return 'gray'
      default:
        return 'gray'
    }
  }

  const formatType = (type: string): string => {
    return type.split('_').map(word =>
      word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ')
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/admin" className="text-secondary" style={{ fontSize: '14px' }}>Admin</Link>
          <h1>Transaction History</h1>
        </div>
      </div>

      {/* Filters */}
      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <select
            className="input select"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
            style={{ width: 200 }}
          >
            <option value="">All Types</option>
            <option value="EARNED">Earned (Job Reward)</option>
            <option value="SPENT">Spent (Job Payment)</option>
            <option value="PRODUCT_PURCHASE">Product Purchase</option>
            <option value="PRODUCT_SALE">Product Sale</option>
            <option value="DEPOSIT">Deposit</option>
            <option value="WITHDRAWAL">Withdrawal</option>
            <option value="ADMIN_GRANT">Admin Grant</option>
            <option value="REFUND">Refund</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {isLoading ? (
        <Loading message="Loading transactions..." />
      ) : transactions.length === 0 ? (
        <div className="empty-state">
          <h3>No transactions found</h3>
        </div>
      ) : (
        <>
          <div className="card-static" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Type</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Description</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Reference</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.75rem', fontSize: '13px' }}>
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontSize: '13px' }}>{tx.user.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{tx.user.email}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <Badge variant={getTypeVariant(tx.type)}>{formatType(tx.type)}</Badge>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'right',
                      fontWeight: 500,
                      color: tx.amount > 0 ? 'var(--success)' : 'var(--error)'
                    }}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount} pts
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {tx.description || '-'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '12px', fontFamily: 'monospace' }}>
                      {tx.referenceId ? tx.referenceId.slice(0, 8) + '...' : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem' }}>
              <button className="pagination-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>
                &lt;
              </button>
              <span style={{ padding: '0 1rem' }}>Page {page} of {totalPages}</span>
              <button className="pagination-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                &gt;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
